import {
  Header,
  SpaceBetween,
  FormField,
  Container,
  Input,
  Select,
  SelectProps,
  Button,
  Box,
  StatusIndicator,
  TextContent,
  Alert,
  ColumnLayout,
  Flashbar,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { EventStepProps } from './components/Common';
import { LocationComponent } from './components/LocationComponent';
import { VisibilityComponent } from './components/VisibilityComponent';
import { AciGrantComponent } from './components/AciGrantComponent';
import { CommunityOrganizationComponent } from './components/CommunityOrganizationComponent';
import { ShiftDetailsComponent } from './components/ShiftDetailsComponent';
import { AdvancedSignupComponent } from './components/AdvancedSignupComponent';
import {
  CoordinatorsPartnersComponent,
  CoordinatorsPartnersProps,
} from './components/CoordinatorsPartnersComponent';
import { ContactsComponent } from './components/ContactsComponent';
import { NotificationPreferencesComponent } from './components/NotificationPreferencesComponent';
import { WritingAssistantPanel } from './components/WritingAssistantPanel';
import { ResizeHandle } from './components/ResizeHandle';
import { SummarizedDescriptionField } from './components/SummarizedDescriptionField';
import { EventInput, EventStatus, ShiftInput, ShiftTimingType } from '@amzn/red-velvet-api';
import { findOption, NotificationsHighlight } from '../../../utils/eventUtils';
import { RichTextEditor } from '../../richTextEditor/richTextEditor';
import { getTextLength } from '../../../utils/htmlText';
import { FIELD_LIMITS, getCharLimitProps } from '../../../utils/fieldConstraints';
import { useWritingAssistant } from '../../../hooks/events/useWritingAssistant';
import { useFeatures, featureIsEnabled } from '../../../hooks/features';
import {
  AI_CONTENT_ASSIST,
  COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL,
  COORDINATOR_SIGNUP_NOTIFICATIONS_EXTERNAL,
} from '../../../utils/constants';

export const getEventDetailsStep = (
  title: string,
  description: string,
  currentFields: EventInput,
  updateFieldHandler: (fields: EventInput) => void,
  metricPrefix: string,
  errors?: Record<string, string>,
  onGrantInfoClick?: () => void,
  onCommunityInfoClick?: () => void,
  eventId?: string
) => {
  return {
    title: title,
    description: description,
    content: (
      <SpaceBetween direction="vertical" size="l">
        <BasicInfoContent
          currentFields={currentFields}
          updateFieldHandler={updateFieldHandler}
          errors={errors}
          metricPrefix={metricPrefix}
          onCommunityInfoClick={onCommunityInfoClick}
          eventId={eventId}
        />
        <AdditionalInfoContent
          currentFields={currentFields}
          updateFieldHandler={updateFieldHandler}
          errors={errors}
          onGrantInfoClick={onGrantInfoClick}
          metricPrefix={metricPrefix}
        />
      </SpaceBetween>
    ),
  };
};
export const getPublishStep = (
  title: string,
  previewText: string,
  openPreviewModal: () => void,
  reviewAndPublishStepProps: ReviewAndPublishStepProps,
  errorText?: string
) => {
  const result = {
    title: title,
    errorText,
    info: (
      <Button data-testid="preview-btn" onClick={openPreviewModal}>
        {previewText}
      </Button>
    ),
    content: (
      <SpaceBetween direction="vertical" size="l">
        {ReviewAndPublishContent(reviewAndPublishStepProps)}
      </SpaceBetween>
    ),
  };
  return result;
};

/////////////////////////////////////////////////////////////////////////////
/////////////////// Event Detail Steps Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

function BasicInfoContent(props: EventStepProps & { onCommunityInfoClick?: () => void }) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const { currentFields, updateFieldHandler, errors, isReadOnly, metricPrefix, eventId } = props;
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const aiEnabled = featureIsEnabled(AI_CONTENT_ASSIST, features, searchParams);

  const [eventTitle, setEventTitle] = useState(currentFields.title ?? '');
  const [eventDescription, setEventDescription] = useState(currentFields.description ?? '');
  const [summarizedDescription, setSummarizedDescription] = useState(
    currentFields.shortDescription ?? ''
  );
  const [titleClearedWarning, setTitleClearedWarning] = useState(false);
  const lastCommittedTitle = useRef(currentFields.title ?? '');

  const ai = useWritingAssistant({
    eventDescription,
    setEventDescription,
    summarizedDescription,
    setSummarizedDescription,
    currentFields,
    updateFieldHandler,
    metricPrefix,
    eventId,
  });

  const updateTextField = () => {
    if (eventTitle.trim() === '' && lastCommittedTitle.current !== '') {
      setEventTitle(lastCommittedTitle.current);
      setTitleClearedWarning(true);
      return;
    }
    setTitleClearedWarning(false);
    lastCommittedTitle.current = eventTitle;
    updateFieldHandler({
      ...currentFields,
      title: eventTitle,
      description: eventDescription,
      shortDescription: summarizedDescription,
    });
  };

  const handleDescriptionBlur = () => {
    updateTextField();
  };

  const titleCharProps = getCharLimitProps(
    eventTitle,
    FIELD_LIMITS.EVENT_TITLE,
    t,
    'eventDetailsStep.eventTitleConstraint'
  );
  const descCharProps = getCharLimitProps(
    eventDescription,
    FIELD_LIMITS.EVENT_DESCRIPTION,
    t,
    'eventDetailsStep.eventDescriptionConstraint',
    getTextLength(eventDescription)
  );

  return (
    <Container header={<Header variant="h2">{t('eventDetailsStep.basicInformation')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.eventTitle')}
          description={t('eventDetailsStep.eventTitleSubText')}
          constraintText={titleCharProps.constraintText}
          errorText={
            titleClearedWarning
              ? t('error.cannotClearEventTitle')
              : (errors?.eventTitle ?? titleCharProps.errorText)
          }
        >
          <Input
            placeholder={t('eventDetailsStep.eventTitlePlaceholder')}
            onChange={(event) => {
              setEventTitle(event.detail.value);
              setTitleClearedWarning(false);
            }}
            onBlur={() => updateTextField()}
            value={eventTitle}
            readOnly={isReadOnly}
          />
        </FormField>
        <FormField
          stretch={aiEnabled && !isReadOnly && !ai.isPanelCollapsed}
          label={t('eventDetailsStep.eventDescription')}
          description={
            <span>
              {t('eventDetailsStep.eventDescriptionSubText')}
              {ai.assistantState === 'tips' && (
                <span
                  className="summarized-description__ai-label"
                  data-testid="desc-ai-generated-label"
                >
                  {' '}
                  <span aria-hidden="true">✦</span> {t('writingAssistant.generatedByAi')}
                </span>
              )}
            </span>
          }
          constraintText={descCharProps.constraintText}
          errorText={errors?.eventDescription ?? descCharProps.errorText}
        >
          {aiEnabled && !isReadOnly ? (
            <div
              className={`writing-assistant-container${ai.isPanelCollapsed ? ' writing-assistant-container--collapsed' : ''}`}
              ref={ai.containerRef}
              style={
                ai.isPanelCollapsed
                  ? undefined
                  : { gridTemplateColumns: `${ai.editorWidthPct}% 3px 1fr` }
              }
            >
              <div className="writing-assistant-container__editor">
                <RichTextEditor
                  value={eventDescription}
                  onChange={ai.handleDescriptionChange}
                  onBlur={handleDescriptionBlur}
                  placeholder={t('eventDetailsStep.eventDescriptionPlaceholder')}
                  readOnly={isReadOnly}
                  hasError={!!errors?.eventDescription || !!descCharProps.errorText}
                  testId="event-description-editor"
                />
              </div>
              {!ai.isPanelCollapsed && (
                <ResizeHandle
                  containerRef={ai.containerRef}
                  currentValue={ai.editorWidthPct}
                  ariaLabel={t('writingAssistant.resizeHandle')}
                  onResize={ai.setEditorWidthPct}
                />
              )}
              <WritingAssistantPanel
                state={ai.assistantState}
                canRewrite={ai.canRewrite}
                rewriteDisabledReason={
                  ai.rewriteDisabledReason ? t(ai.rewriteDisabledReason) : undefined
                }
                isCollapsed={ai.isPanelCollapsed}
                onToggleCollapse={ai.handleTogglePanel}
                rewrittenDescription={ai.rewrittenDescription}
                tips={ai.tips}
                isTipsPending={ai.isTipsPending}
                tipsError={ai.tipsError}
                errorMessage={ai.assistantError}
                isRewriteValid={ai.isRewriteValid}
                rewriteRejection={ai.rewriteRejection}
                onRewrite={ai.handleRewrite}
                onAccept={ai.handleAcceptRewrite}
                onCancel={ai.handleCancelRewrite}
                onCancelGeneration={ai.handleCancelGeneration}
                onGenerateAnother={ai.handleRewrite}
              />
            </div>
          ) : (
            <RichTextEditor
              value={eventDescription}
              onChange={(html) => setEventDescription(html)}
              onBlur={handleDescriptionBlur}
              placeholder={t('eventDetailsStep.eventDescriptionPlaceholder')}
              readOnly={isReadOnly}
              hasError={!!errors?.eventDescription || !!descCharProps.errorText}
              testId="event-description-editor"
            />
          )}
        </FormField>
        <SummarizedDescriptionField
          value={summarizedDescription}
          onChange={(val) => setSummarizedDescription(val)}
          onBlur={() => updateTextField()}
          aiEnabled={aiEnabled && !isReadOnly}
          isGenerating={ai.isGeneratingShortDesc}
          canGenerate={ai.canRewrite}
          generateDisabledReason={
            ai.rewriteDisabledReason === 'writingAssistant.maxCharsHint'
              ? t(ai.rewriteDisabledReason)
              : undefined
          }
          hasGenerationError={ai.shortDescError}
          onGenerate={() => ai.triggerShortDescGeneration()}
          isAiGenerated={ai.isShortDescAiGenerated}
          onUserEdit={ai.clearAiGeneratedLabel}
          errorText={errors?.summarizedDescription}
          readOnly={isReadOnly}
        />
        <CommunityOrganizationComponent {...props} />
        <LocationComponent {...props} />
        <VisibilityComponent {...props} />
      </SpaceBetween>
    </Container>
  );
}

function AdditionalInfoContent(props: EventStepProps & { onGrantInfoClick?: () => void }) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const focusOptions = t('focus', { returnObjects: true }) as SelectProps.Options;
  const primaryActivityOptions = t('activity', { returnObjects: true }) as SelectProps.Options;

  const [focusArea, setFocusArea] = useState<OptionDefinition | null>(
    props.currentFields.focusArea ? findOption(focusOptions, props.currentFields.focusArea) : null
  );
  const [primaryActivity, setPrimaryActivity] = useState<OptionDefinition | null>(
    props.currentFields.primaryActivity
      ? findOption(primaryActivityOptions, props.currentFields.primaryActivity)
      : null
  );

  return (
    <Container header={<Header variant="h2">{t('eventDetailsStep.additionalInfo')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.focusArea')}
          description={t('eventDetailsStep.focusAreaSubText')}
          errorText={props.errors?.focusArea}
        >
          <Select
            onChange={(event) => {
              setFocusArea(event.detail.selectedOption);
              props.updateFieldHandler({
                ...props.currentFields,
                focusArea: event.detail.selectedOption.value ?? '',
              });
            }}
            selectedOption={focusArea}
            options={focusOptions}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.primaryActivity')}
          description={t('eventDetailsStep.primaryActivitySubText')}
          errorText={props.errors?.primaryActivity}
        >
          <Select
            onChange={(event) => {
              setPrimaryActivity(event.detail.selectedOption);
              props.updateFieldHandler({
                ...props.currentFields,
                primaryActivity: event.detail.selectedOption.value ?? '',
              });
            }}
            selectedOption={primaryActivity}
            options={primaryActivityOptions}
            placeholder={t('eventDetailsStep.primaryActivityPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <AciGrantComponent {...props} onGrantInfoClick={props.onGrantInfoClick} />
      </SpaceBetween>
    </Container>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Shift Details Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

export interface ShiftWaitlistStepProps {
  /** Opens the waitlist info help panel. */
  onInfoClick?: () => void;
  /** Edit flow: the event's active waitlist is still loading. */
  loading?: boolean;
  /** Edit flow: the active-waitlist fetch failed. */
  error?: boolean;
  /** Edit flow: number of ACTIVE waitlist entries for a given shiftId. */
  activeCountByShiftId?: (shiftId: string) => number;
  /** Edit flow: invoked with the shiftId when the user turns a shift's waitlist off while entries exist. */
  onAttemptDisable?: (shiftId: string) => void;
}

export interface ShiftStepProps {
  currentFields: ShiftInput[];
  updateFieldHandler: (fields: ShiftInput[], changedIndex?: number, removedIndex?: number) => void;
  eventFields: EventInput;
  updateEventFieldHandler: (fields: EventInput) => void;
  errors?: Record<string, string>;
  isReadOnly?: boolean;
  shiftIdsWithSignups?: Set<string>;
  onShiftTypeInfoClick?: () => void;
  metricPrefix: string;
  waitlistStep?: ShiftWaitlistStepProps;
  /** Create flow: default an unset capped shift's waitlist ON regardless of shiftId (see ShiftDetailsComponent). */
  isCreateFlow?: boolean;
}

export const getShiftStep = (
  title: string,
  description: string,
  currentFields: ShiftInput[],
  updateFieldHandler: (fields: ShiftInput[], changedIndex?: number, removedIndex?: number) => void,
  eventFields: EventInput,
  updateEventFieldHandler: (fields: EventInput) => void,
  metricPrefix: string,
  errors?: Record<string, string>,
  shiftIdsWithSignups?: Set<string>,
  onShiftTypeInfoClick?: () => void,
  waitlistStep?: ShiftWaitlistStepProps,
  isCreateFlow = false
) => {
  return {
    title,
    description,
    content: (
      <ShiftStepContent
        currentFields={currentFields}
        updateFieldHandler={updateFieldHandler}
        eventFields={eventFields}
        updateEventFieldHandler={updateEventFieldHandler}
        errors={errors}
        shiftIdsWithSignups={shiftIdsWithSignups}
        onShiftTypeInfoClick={onShiftTypeInfoClick}
        metricPrefix={metricPrefix}
        waitlistStep={waitlistStep}
        isCreateFlow={isCreateFlow}
      />
    ),
  };
};

function ShiftStepContent({
  currentFields,
  updateFieldHandler,
  eventFields,
  updateEventFieldHandler,
  errors,
  isReadOnly,
  shiftIdsWithSignups,
  onShiftTypeInfoClick,
  metricPrefix,
  waitlistStep,
  isCreateFlow,
}: ShiftStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const [allExpanded, setAllExpanded] = useState(true);
  const [expandToggleKey, setExpandToggleKey] = useState(0);

  const nextId = useRef(currentFields.length);
  const shiftKeys = useRef<number[]>(currentFields.map((_, i) => i));

  const getKey = (index: number): number => {
    while (shiftKeys.current.length <= index) {
      shiftKeys.current.push(nextId.current++);
    }
    return shiftKeys.current[index];
  };

  const updateShift = (index: number, updated: ShiftInput) => {
    const newShifts = [...currentFields];
    newShifts[index] = updated;
    updateFieldHandler(newShifts, index);
  };

  const removeShift = (index: number) => {
    shiftKeys.current.splice(index, 1);
    const newShifts = currentFields.filter((_, i) => i !== index);
    updateFieldHandler(newShifts, undefined, index);
  };

  const addShift = () => {
    const midnight = new Date(new Date().toDateString());
    updateFieldHandler([
      ...currentFields,
      {
        startDateTime: midnight,
        endDateTime: midnight,
        useEventLocation: true,
        shiftTimingType: ShiftTimingType.SCHEDULED,
        shiftLength: 0,
        // No waitlistEnabled seed: a new shift starts unlimited; ShiftDetailsComponent defaults the
        // toggle ON for a new (no-shiftId) shift once capped, and applyWaitlistEnabledDefault
        // materializes the value on submit. Seeding true here is auto-off'd while unlimited.
      },
    ]);
  };

  const toggleAll = () => {
    setAllExpanded((prev) => !prev);
    setExpandToggleKey((prev) => prev + 1);
  };

  const fieldsRef = useRef(currentFields);
  fieldsRef.current = currentFields;
  const handlerRef = useRef(updateFieldHandler);
  handlerRef.current = updateFieldHandler;

  const eventFieldsRef = useRef(eventFields);
  eventFieldsRef.current = eventFields;
  const eventHandlerRef = useRef(updateEventFieldHandler);
  eventHandlerRef.current = updateEventFieldHandler;

  const stableUpdateEventFields = useCallback((overrides: EventInput) => {
    eventHandlerRef.current({ ...eventFieldsRef.current, ...overrides });
  }, []);

  return (
    <Box>
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <Button variant="normal" onClick={toggleAll}>
                  {allExpanded ? t('shiftStep.collapseAll') : t('shiftStep.expandAll')}
                </Button>
              }
            >
              {t('shiftStep.eventShifts')}
            </Header>
          }
        >
          <SpaceBetween size="l">
            {currentFields.map((shift, index) => (
              <ShiftDetailsComponent
                key={getKey(index)}
                shift={shift}
                index={index}
                updateShift={updateShift}
                onRemove={
                  currentFields.length > 1 &&
                  !(shift.shiftId && shiftIdsWithSignups?.has(shift.shiftId))
                    ? removeShift
                    : undefined
                }
                errors={errors}
                expandOverride={allExpanded}
                expandToggleKey={expandToggleKey}
                isReadOnly={isReadOnly}
                eventFields={eventFields}
                onShiftTypeInfoClick={onShiftTypeInfoClick}
                metricPrefix={metricPrefix}
                onWaitlistInfoClick={waitlistStep?.onInfoClick}
                waitlistInfoLoading={waitlistStep?.loading}
                waitlistInfoError={waitlistStep?.error}
                waitlistActiveCount={
                  shift.shiftId ? (waitlistStep?.activeCountByShiftId?.(shift.shiftId) ?? 0) : 0
                }
                onAttemptDisableWaitlist={waitlistStep?.onAttemptDisable}
                isCreateFlow={isCreateFlow}
              />
            ))}
            {!isReadOnly && (
              <Button
                iconName="add-plus"
                variant="link"
                onClick={addShift}
                data-aci-analytics-name={`${metricPrefix}_shiftAdded`}
              >
                {t('shiftStep.addShift')}
              </Button>
            )}
          </SpaceBetween>
        </Container>
        <AdvancedSignupComponent
          eventFields={eventFields}
          updateEventFields={stableUpdateEventFields}
          isReadOnly={isReadOnly}
          metricPrefix={metricPrefix}
          errors={errors}
        />
      </SpaceBetween>
    </Box>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Team & Partnership Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

export const getTeamAndPartnershipStep = (
  title: string,
  description: string,
  currentFields: EventInput,
  updateFieldHandler: (fields: EventInput) => void,
  metricPrefix: string,
  errors?: Record<string, string>,
  ownerAlias?: string,
  eventStatus?: EventStatus,
  notificationsHighlight?: NotificationsHighlight,
  onCoordinatorNotificationInfoClick?: () => void
) => {
  return {
    title,
    description,
    content: (
      <TeamAndPartnershipStepContent
        currentFields={currentFields}
        updateFieldHandler={updateFieldHandler}
        errors={errors}
        metricPrefix={metricPrefix}
        ownerAlias={ownerAlias}
        eventStatus={eventStatus}
        notificationsHighlight={notificationsHighlight}
        onCoordinatorNotificationInfoClick={onCoordinatorNotificationInfoClick}
      />
    ),
    isOptional: false,
  };
};

function TeamAndPartnershipStepContent(
  props: CoordinatorsPartnersProps & { notificationsHighlight?: NotificationsHighlight }
) {
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const internalCoordinatorNotificationsEnabled = featureIsEnabled(
    COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL,
    features,
    searchParams
  );
  const externalCoordinatorNotificationsEnabled = featureIsEnabled(
    COORDINATOR_SIGNUP_NOTIFICATIONS_EXTERNAL,
    features,
    searchParams
  );

  return (
    <SpaceBetween size="l">
      <CoordinatorsPartnersComponent {...props} />
      <ContactsComponent {...props} />
      {internalCoordinatorNotificationsEnabled && (
        <NotificationPreferencesComponent
          currentFields={props.currentFields}
          updateFieldHandler={props.updateFieldHandler}
          errors={props.errors}
          isReadOnly={props.isReadOnly}
          metricPrefix={props.metricPrefix}
          ownerAlias={props.ownerAlias}
          includeExternalRecipients={externalCoordinatorNotificationsEnabled}
          highlightOnMount={props.notificationsHighlight?.active}
          onHighlightShown={props.notificationsHighlight?.onShown}
          onCoordinatorNotificationInfoClick={props.onCoordinatorNotificationInfoClick}
        />
      )}
    </SpaceBetween>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Review And Publish Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

interface ReviewAndPublishStepProps {
  currentEventFields: EventInput;
  currentShiftFields: ShiftInput[];
  updateEventFieldHandler: (fields: EventInput) => void;
  updateShiftFieldHandler: (
    fields: ShiftInput[],
    changedIndex?: number,
    removedIndex?: number
  ) => void;
  setActiveStepIndex: (index: number) => void;
  errors?: Record<string, string>;
  publicationStatus?: 'draft' | 'pending' | 'published' | 'unpublished';
  totalSignupCount?: number;
  submitError?: string | null;
  onDismissError?: () => void;
  onDismissPublishError?: () => void;
  metricPrefix: string;
  ownerAlias?: string;
  eventStatus?: EventStatus;
}

function ReviewAndPublishContent(props: ReviewAndPublishStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const {
    currentEventFields,
    currentShiftFields,
    updateShiftFieldHandler,
    updateEventFieldHandler,
    errors,
    setActiveStepIndex,
  } = props;
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const internalCoordinatorNotificationsEnabled = featureIsEnabled(
    COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL,
    features,
    searchParams
  );
  const externalCoordinatorNotificationsEnabled = featureIsEnabled(
    COORDINATOR_SIGNUP_NOTIFICATIONS_EXTERNAL,
    features,
    searchParams
  );

  return (
    <Box>
      <SpaceBetween size="xxl">
        {/* Event Details Section */}
        <SpaceBetween size="xs" data-testid="step-1-review">
          <Container>
            <ColumnLayout
              columns={2}
              borders={(props.totalSignupCount ?? 0) > 0 ? 'vertical' : undefined}
            >
              <SpaceBetween size="xxxs">
                <TextContent>
                  <b>{t('reviewPublishStep.publication')}</b>
                </TextContent>
                {props.publicationStatus === 'published' ? (
                  <StatusIndicator type="success">
                    {t('reviewPublishStep.published')}
                  </StatusIndicator>
                ) : props.publicationStatus === 'pending' ? (
                  <StatusIndicator type="warning">
                    {t('reviewPublishStep.pendingChanges')}
                  </StatusIndicator>
                ) : props.publicationStatus === 'unpublished' ? (
                  <StatusIndicator type="stopped">
                    {t('reviewPublishStep.unpublished')}
                  </StatusIndicator>
                ) : (
                  <StatusIndicator type="in-progress">
                    {t('reviewPublishStep.draft')}
                  </StatusIndicator>
                )}
              </SpaceBetween>
              {(props.totalSignupCount ?? 0) > 0 && (
                <SpaceBetween size="xxxs">
                  <TextContent>
                    <b>{t('reviewPublishStep.signups')}</b>
                  </TextContent>
                  <TextContent>
                    {t('reviewPublishStep.signupCount', { count: props.totalSignupCount })}
                  </TextContent>
                </SpaceBetween>
              )}
            </ColumnLayout>
          </Container>
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="event-edit-step-btn" onClick={() => setActiveStepIndex(0)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('eventDetailsStep.eventDetails')}
          </Header>
          <BasicInfoContent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
          <AdditionalInfoContent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
        </SpaceBetween>
        {/* Shift Details Section */}
        <SpaceBetween size="xs" data-testid="step-2-review">
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="shift-edit-step-btn" onClick={() => setActiveStepIndex(1)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('shiftStep.stepTitle')}
          </Header>
          <ShiftStepContent
            currentFields={currentShiftFields}
            updateFieldHandler={updateShiftFieldHandler}
            eventFields={currentEventFields}
            updateEventFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
        </SpaceBetween>
        {/* Team & Partnership Details Section */}
        <SpaceBetween size="xs" data-testid="step-3-review">
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="team-edit-step-btn" onClick={() => setActiveStepIndex(2)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('teamPartnershipStep.stepTitle')}
          </Header>
          <CoordinatorsPartnersComponent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
            ownerAlias={props.ownerAlias}
            eventStatus={props.eventStatus}
          />
          <ContactsComponent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
            metricPrefix={props.metricPrefix}
          />
          {internalCoordinatorNotificationsEnabled && (
            <NotificationPreferencesComponent
              currentFields={currentEventFields}
              updateFieldHandler={updateEventFieldHandler}
              errors={errors}
              isReadOnly
              metricPrefix={props.metricPrefix}
              ownerAlias={props.ownerAlias}
              includeExternalRecipients={externalCoordinatorNotificationsEnabled}
            />
          )}
        </SpaceBetween>
        {props.errors?.publishError !== undefined && (
          <Alert
            type="error"
            header={t('error.publishErrorHeader')}
            dismissible={!!props.onDismissPublishError}
            onDismiss={props.onDismissPublishError}
          >
            {props.errors.publishError}
          </Alert>
        )}
        {props.submitError && (
          <Flashbar
            items={[
              {
                type: 'error',
                header: t('error.publishFailedHeader'),
                content: props.submitError,
                dismissible: !!props.onDismissError,
                onDismiss: props.onDismissError,
              },
            ]}
          />
        )}
      </SpaceBetween>
    </Box>
  );
}
